import { useState, useEffect } from "react";
import { useUser } from "@supabase/auth-helpers-react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { LineItemForm } from "./line-items/LineItemForm";
import { LineItemCard } from "./line-items/LineItemCard";
import { useTranslation } from "react-i18next";

interface Template {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  currency: "EUR" | "GBP" | "USD";
  created_at?: string;
  updated_at?: string;
  user_id?: string;
}

export function LineItemTemplates() {
  const user = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const { t } = useTranslation();

  const fetchTemplates = async () => {
    try {
      if (!user) return;

      const { data, error } = await supabase
        .from("line_item_templates")
        .select("*")
        .eq("user_id", user.id);

      if (error) throw error;
      setTemplates(data);
    } catch (error: any) {
      toast.error(t("toasts.template_load_failed", "Failed to load template: "), {
        description: error.message,
      });
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [user]); // Fetch templates when user changes

  const onSubmit = async (formData: any) => {
    try {
      setIsLoading(true);
      if (!user) return;

      const templateData = {
        user_id: user.id,
        description: formData.description,
        quantity: formData.quantity,
        unit_price: formData.unit_price,
        currency: formData.currency,
      };

      if (editingTemplate) {
        const { error } = await supabase
          .from("line_item_templates")
          .update(templateData)
          .eq("id", editingTemplate.id);

        if (error) throw error;
        toast.success(t("toasts.template_updated_success", "Template updated successfully"));
      } else {
        const { error } = await supabase
          .from("line_item_templates")
          .insert(templateData);

        if (error) throw error;
        toast.success(t("toasts.template_created_success", "Template created successfully"));
      }

      setIsDialogOpen(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error: any) {
      toast.error(editingTemplate ? t("toasts.template_updated_failed", "Failed to update template: " ) : t("toasts.template_created_failed", "Failed to create template: " ), {
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase
        .from("line_item_templates")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success(t("toasts.template_delete_success", "Template deleted successfully"));
      fetchTemplates();
    } catch (error: any) {
      toast.error(t("toasts.template_delete_failed", "Failed to delete template: " ), {
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (template: Template) => {
    setEditingTemplate(template);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingTemplate(null);
  };

  const handleAddClick = () => {
    setEditingTemplate(null);
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="space-y-3 md:space-y-4">
        {templates.map((template) => (
          <LineItemCard
            key={template.id}
            template={template}
            onEdit={handleEdit}
            onDelete={deleteTemplate}
          />
        ))}
      </div>

      <div className="flex justify-center sm:justify-end">
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="w-full sm:w-auto bg-[#5E64A7] hover:bg-[#5CC2BB] hover:text-[#34375B] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200" 
              onClick={handleAddClick}
            >
              <Plus className="w-4 h-4 mr-2" />
              {t("templates.add_template", "Add Template")}
            </Button>
          </DialogTrigger>
          <DialogContent className="w-[95vw] max-w-md sm:max-w-lg md:max-w-xl">
            <DialogHeader>
              <DialogTitle className="text-[#34375B]">
                {editingTemplate ? t("templates.edit_line_item_template", "Edit Line Item Template") : t("templates.create_line_item_template", "Create Line Item Template")}
              </DialogTitle>
            </DialogHeader>
            <LineItemForm
              onSubmit={onSubmit}
              editingTemplate={editingTemplate}
              isLoading={isLoading}
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
